import { isNil } from 'lodash';
import { FunctionComponent, SVGProps } from 'react';

import { ReactComponent as FitnessBadgeSvg } from 'assets/images/collection/placeholder-fitness.svg';
import { ReactComponent as FoodBadgeSvg } from 'assets/images/collection/placeholder-food.svg';
import { ReactComponent as OfficeBadgeSvg } from 'assets/images/collection/placeholder-office.svg';
import DigitalHealthBg from 'assets/images/collection/bg-digital_health.png';
import FitnessActivitiesBg from 'assets/images/collection/bg-fitness_activities.png';
import FitnessEquipmentBg from 'assets/images/collection/bg-fitness_equipment.png';
import FoodServicesBg from 'assets/images/collection/bg-food_services.png';
import HomeOfficeBg from 'assets/images/collection/bg-home_office.png';
import WellbeingServicesBg from 'assets/images/collection/bg-wellbeing_services.png';
import { IEmployeeWallet } from 'hooks/useQueryProfile';

interface Subcategory {
  eligible: IEmployeeWallet['company_wallet_configuration']['categories'][0]['subcategories'][0]['eligible'];
  value: IEmployeeWallet['company_wallet_configuration']['categories'][0]['subcategories'][0]['value'];
}

export interface Category {
  alias?: IEmployeeWallet['company_wallet_configuration']['categories'][0]['alias'];
  id: IEmployeeWallet['company_wallet_configuration']['categories'][0]['id'];
  name: IEmployeeWallet['company_wallet_configuration']['categories'][0]['name'];
  subcategories?: Array<Subcategory>;
}

export const enum CategoryId {
  CAREGIVING = 'caregiving',
  DIGITAL_HEALTH = 'digital_health',
  FINANCIAL_WELLBEING = 'financial_wellbeing',
  FITNESS_ACCESSORIES = 'fitness_accessories',
  FITNESS_ACTIVITIES = 'fitness_activities',
  FITNESS_EQUIPMENT = 'fitness_equipment',
  FOOD = 'food',
  FOOD_SERVICES = 'food_services',
  HOME_EQUIPMENT = 'home_equipment',
  HOME_OFFICE = 'home_office',
  MEDICAL_SERVICES = 'medical_services',
  OFFICE_SUPPLIES = 'office_supplies',
  OTHERS = 'others',
  SKILLS_DEVELOPMENT = 'skills_development',
  WELLBEING_SERVICES = 'wellbeing_services',
}

export const TARGET_CATEGORIES_MAP: {
  [key: string]: {
    id: string;
    label: string;
  };
} = Object.freeze({
  digital_health: {
    id: CategoryId.DIGITAL_HEALTH,
    label: 'Digital Health',
  },
  fitness_activities: {
    id: CategoryId.FITNESS_ACTIVITIES,
    label: 'Gym and Fitness',
  },
  caregiving: {
    id: CategoryId.CAREGIVING,
    label: 'Family',
  },
  food_services: {
    id: CategoryId.FOOD_SERVICES,
    label: 'Food',
  },
  wellbeing_services: {
    id: CategoryId.WELLBEING_SERVICES,
    label: 'Wellbeing',
  },
});

export const ELIGIBLE_CATEGORIES = [
  'fitness_accessories',
  'fitness_activities',
  'fitness_equipment',
  'food_services',
  'home_office',
  'wellbeing_services',
];

export const POPULAR_CATEGORIES: {
  [key: string]: {
    background_image: string;
    description: string;
    id: string;
    isEligible: (category?: Category) => boolean;
    pill_icon: FunctionComponent<SVGProps<SVGSVGElement>>;
    priority: number;
    title: string;
  };
} = Object.freeze({
  digital_health: {
    background_image: DigitalHealthBg,
    description: 'Deals on fitness, routine, and meditation apps',
    id: CategoryId.DIGITAL_HEALTH,
    isEligible: (category?: Category) => !isNil(category),
    pill_icon: FoodBadgeSvg,
    priority: 6,
    title: 'Goodbye to stress',
  },
  fitness_activities: {
    background_image: FitnessActivitiesBg,
    description: 'Get discounts on gyms and class subscriptions!',
    id: CategoryId.FITNESS_ACTIVITIES,
    isEligible: (category?: Category) => !isNil(category),
    pill_icon: FitnessBadgeSvg,
    priority: 4,
    title: 'Gyms and classes',
  },
  fitness_equipment: {
    background_image: FitnessEquipmentBg,
    description: 'Build your home gym or save on a smart watch!',
    id: CategoryId.FITNESS_EQUIPMENT,
    isEligible: (category?: Category) => !isNil(category),
    pill_icon: FitnessBadgeSvg,
    priority: 2,
    title: 'Fitness equipment',
  },
  food_services: {
    background_image: FoodServicesBg,
    description: 'Get meal kits, groceries, or your favorite takeout.',
    id: CategoryId.FOOD_SERVICES,
    // We want the category to be eligible if its sole subcategory is not "nutrition_program".
    isEligible: (category?: Category) => {
      if (isNil(category) || isNil(category.subcategories)) {
        return false;
      }
      const subcategories = category.subcategories.filter(
        ({ eligible }) => eligible
      );
      return (
        subcategories.length > 1 ||
        subcategories[0]?.value !== 'nutrition_program'
      );
    },
    pill_icon: FoodBadgeSvg,
    priority: 1,
    title: 'Food delivery',
  },
  home_office: {
    background_image: HomeOfficeBg,
    description: 'Deals on desks, chairs, and laptop accessories',
    id: CategoryId.HOME_OFFICE,
    isEligible: (category?: Category) => !isNil(category),
    pill_icon: OfficeBadgeSvg,
    priority: 3,
    title: 'Home office',
  },
  wellbeing_services: {
    background_image: WellbeingServicesBg,
    description: 'Discounts on massage and relaxation equipment',
    id: CategoryId.WELLBEING_SERVICES,
    isEligible: (category?: Category) => !isNil(category),
    pill_icon: OfficeBadgeSvg,
    priority: 5,
    title: 'Wellbeing',
  },
});

// NOTE: this is a sorted list and order matters
// used on store landing page and any other components that looks up against list of top categories per spend
// https://docs.google.com/spreadsheets/d/1LOUafnQsAb-U7Hnvc_A43fdUOGEVtio-Z97ww9RU72g/edit#gid=0
export const TOP_CATEGORIES: Array<{
  id: CategoryId;
  name: string;
}> = [
  { id: CategoryId.FOOD_SERVICES, name: 'Food Services' },
  { id: CategoryId.HOME_OFFICE, name: 'Home Office' },
  { id: CategoryId.FITNESS_EQUIPMENT, name: 'Fitness Equipment' },
  { id: CategoryId.FITNESS_ACCESSORIES, name: 'Fitness Accessories' },
  { id: CategoryId.DIGITAL_HEALTH, name: 'Digital Health' },
  { id: CategoryId.WELLBEING_SERVICES, name: 'Wellbeing Services' },
  { id: CategoryId.FITNESS_ACTIVITIES, name: 'Fitness Activities' },
  { id: CategoryId.HOME_EQUIPMENT, name: 'Home Equipment' },
  { id: CategoryId.SKILLS_DEVELOPMENT, name: 'Skills Development' },
  { id: CategoryId.OTHERS, name: 'Miscellaneous' },
  { id: CategoryId.FOOD, name: 'Food' },
  { id: CategoryId.MEDICAL_SERVICES, name: 'Medical Services' },
  { id: CategoryId.FINANCIAL_WELLBEING, name: 'Financial Wellbeing' },
];

export const enum CategorySortValue {
  NEW_ARRIVALS = 'recency',
  BEST_SELLERS = 'popularity',
  PRICE_ASC = 'price-asc',
  PRICE_DESC = 'price-desc',
}

export const CATEGORY_SORT_OPTIONS: Array<{
  value: CategorySortValue;
  name: string;
}> = [
  { value: CategorySortValue.NEW_ARRIVALS, name: 'New Arrivals' },
  { value: CategorySortValue.BEST_SELLERS, name: 'Best Sellers' },
  { value: CategorySortValue.PRICE_ASC, name: 'Price - Low to High' },
  { value: CategorySortValue.PRICE_DESC, name: 'Price - High to Low' },
];

const PLYTIX_PREFIX =
  'https://files.plytix.com/api/v1.1/file/public_files/pim/assets';
const SUBCATEGORIES_PREFIX = 'dd/18/4f/62/624f18ddacfa66ab0664e612/images';

export const HOME_OFFICE_SUBCATEGORIES_MAP: Array<{
  label: string;
  subcategories: Array<string>;
  subtitle: string;
  title: string;
  url: string;
}> = [
  {
    label: 'Desks & Chairs',
    subcategories: ['desks_office_chairs'],
    subtitle:
      'Get started with a chair or a desk that’s right for you and your space',
    title: 'Get Comfortable',
    url: `${PLYTIX_PREFIX}/${SUBCATEGORIES_PREFIX}/4e/25/dc/63/63dc254e1f86b54157c661a1/subcategory_chair.png`,
  },
  {
    label: 'Headphones',
    subcategories: ['headphones'],
    subtitle: 'Find the best headset to stay focused and connected',
    title: 'Stay Concentrated',
    url: `${PLYTIX_PREFIX}/${SUBCATEGORIES_PREFIX}/c4/26/dc/63/63dc26c445952636f4887162/subcategory_headphones.png`,
  },
  {
    label: 'Tech Gadgets',
    subcategories: ['computer_laptop_accessories'],
    subtitle: 'Select great tech gadgets to maximize your productivity',
    title: 'Boost Productivity',
    url: `${PLYTIX_PREFIX}/${SUBCATEGORIES_PREFIX}/4f/25/dc/63/63dc254f03648fd590003f90/subcategory_mouse.png`,
  },
  {
    label: 'Accessories',
    subcategories: ['cables_cords', 'desktop_equipment'],
    subtitle:
      'Discover new accessories to keep oganized and physically healthy',
    title: 'Keep Organized',
    url: `${PLYTIX_PREFIX}/${SUBCATEGORIES_PREFIX}/4d/25/dc/63/63dc254d45952636f4887130/subcategory_accesories.png`,
  },
];

const VENDOR_PREFIX = 'dd/18/4f/62/624f18ddacfa66ab0664e612/images';
export const HOME_OFFICE_VENDORS_MAP: {
  [key: string]: string;
} = Object.freeze({
  bose: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/08/eb/ef/63/63efeb08ae6d21396fc6341b/brandTile_bose.png`,
  cleer: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/94/00/f0/63/63f0009412652121969d6168/brandTile_cleer.png`,
  dell: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/09/eb/ef/63/63efeb0931aaaecc7764dc9b/brandTile_dell.png`,
  'flexi-spot': `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/09/eb/ef/63/63efeb09685ae95bd6bf660e/brandTile_flexispot.png`,
  'fluid-stance': `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/09/eb/ef/63/63efeb0931aaaecc7764dc9c/brandTile_fluidstance.png`,
  ergoworks: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/09/eb/ef/63/63efeb09ae6d21396fc6341c/brandTile_ergoworks.png`,
  gamdias: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0a/eb/ef/63/63efeb0a685ae95bd6bf660f/brandTile_gamdias.png`,
  lifespan: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0a/eb/ef/63/63efeb0aae6d21396fc6341d/brandTile_lifespan.png`,
  'lume-cube': `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0b/eb/ef/63/63efeb0b1292792ac38b322e/brandTile_lumecube.png`,
  moft: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0b/eb/ef/63/63efeb0b31aaaecc7764dc9d/brandTile_moft.png`,
  porvata: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0b/eb/ef/63/63efeb0b5969a63f3a552449/brandTile_porvata.png`,
  shockz: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0c/eb/ef/63/63efeb0c1292792ac38b322f/brandTile_shokz.png`,
  sony: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0d/eb/ef/63/63efeb0d31aaaecc7764dc9e/brandTile_sony.png`,
  vivo: `${PLYTIX_PREFIX}/${VENDOR_PREFIX}/0d/eb/ef/63/63efeb0d5969a63f3a55244a/brandTile_vivo.png`,
});
